import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Search, RefreshCw, Wallet, AlertTriangle } from "lucide-react";
import { PriceChart } from "../components/PriceChart";

// 2026-10-26 — unified Manual Order terminal (chat: "Able to place manual order
// for commodities but not for Stocks ... for other users. Need to improve
// Manual order frontend" + broker-app screenshots). One page for every Kite
// account the viewer can access (admin: all; others: their own), stock/index
// options (NFO) and commodity options (MCX). Data: /kite-console/chain,
// /kite-console/quote (trade_terminal.py). Orders: /kite-console/{acc}/order
// (kite_console.place_manual_order — same safety rules as before).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MCX = ["CRUDEOIL", "CRUDEOILM", "GOLDM", "SILVERM", "NATURALGAS", "NATGASMINI"];
const NFO_QUICK = ["NIFTY", "BANKNIFTY"];
const errMsg = (e, d) => e?.response?.data?.detail || d;
const fmt = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
const fmtQty = (v) => (v == null ? "—" : Number(v) >= 1e5 ? `${(Number(v) / 1e5).toFixed(2)}L` : fmt(v, 0));
const chgColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");
const Chg = ({ v }) => (v == null ? <span className="text-muted-3">—</span>
  : <span style={{ color: chgColor(v) }}>{v >= 0 ? "+" : ""}{fmt(v)}%</span>);
const pill = "px-3 py-1.5 border text-[11px] font-mono-data uppercase tracking-wider";
const ITM_BG = "rgba(240, 180, 40, 0.06)";
const TH_BG = { background: "var(--bg-primary, #0b0b0b)" };
const TH_BG2 = { ...TH_BG, boxShadow: "inset 0 -1px 0 var(--grid, #2a2a2a)" };

const useVisiblePoll = (fn, ms, deps) => {
  useEffect(() => {
    let alive = true;
    const run = () => { if (alive && document.visibilityState === "visible") fn(); };
    fn();
    const id = setInterval(run, ms);
    return () => { alive = false; clearInterval(id); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

// ───────────── option chain ─────────────
const Leg = ({ leg, side, maxOi, onPick, selected }) => {
  if (!leg) return <td colSpan={3} className="px-2 py-1.5 text-center text-muted-3">—</td>;
  const pct = maxOi ? Math.min(100, ((leg.oi_lots || 0) / maxOi) * 100) : 0;
  // 2026-11-02 — OI bars a little brighter (were 0.18 alpha; hard to read on the dark theme)
  const bar = side === "CE" ? "rgba(46, 204, 113, 0.34)" : "rgba(231, 76, 60, 0.34)";
  const bg = leg.itm ? ITM_BG : undefined;
  const cellStyle = { background: selected ? "rgba(255,255,255,0.08)" : bg, opacity: leg.dead ? 0.38 : 1 };
  const oiCell = (
    <td key="oi" className="px-2 py-1.5 relative text-right" style={cellStyle} title={leg.dead ? leg.dead_reason : undefined}>
      <div className="absolute inset-y-1" style={{ [side === "CE" ? "right" : "left"]: 0, width: `${pct}%`, background: bar }} />
      <span className="relative">{fmtQty(leg.oi_lots)}</span>
    </td>
  );
  const ltpCell = (
    <td key="ltp" className="px-2 py-1.5 text-right cursor-pointer hover:underline" style={cellStyle}
        onClick={() => onPick(leg, "BUY")} title={leg.dead ? leg.dead_reason : "Open contract"}>
      <div>{leg.ltp != null ? fmt(leg.ltp) : "—"}</div>
      <div className="text-[9px]"><Chg v={leg.chg_pct} /></div>
    </td>
  );
  const bs = (
    <td key="bs" className="px-1 py-1.5 text-center whitespace-nowrap" style={cellStyle}>
      <button className="px-1.5 py-0.5 border text-[9px] mr-1" style={{ borderColor: "var(--bull)", color: "var(--bull)" }}
              onClick={() => onPick(leg, "BUY")} data-testid={`mo-${side}-buy-${leg.tradingsymbol}`}>B</button>
      <button className="px-1.5 py-0.5 border text-[9px]" style={{ borderColor: "var(--bear)", color: "var(--bear)" }}
              onClick={() => onPick(leg, "SELL")} data-testid={`mo-${side}-sell-${leg.tradingsymbol}`}>S</button>
    </td>
  );
  return side === "CE" ? <>{oiCell}{ltpCell}{bs}</> : <>{bs}{ltpCell}{oiCell}</>;
};

const Chain = ({ data, onPick, selectedTs }) => {
  if (!data) return <div className="border border-grid p-6 text-xs text-muted-3 font-mono-data">Loading chain…</div>;
  if (!data.rows?.length) return <div className="border border-grid p-6 text-xs text-muted-3 font-mono-data">{data.error || "No strikes"}</div>;
  const sp = data.spot || {};
  const marker = (
    <tr key="spot" data-testid="mo-spot-marker">
      <td colSpan={7} className="py-1">
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t" style={{ borderColor: "var(--warn, #f39c12)" }} />
          <span className="px-3 py-0.5 text-[11px] font-mono-data font-semibold"
                style={{ background: "var(--warn, #f39c12)", color: "#111" }}>
            {fmt(sp.ltp)} <span className="font-normal">({sp.chg_pct >= 0 ? "+" : ""}{fmt(sp.chg_pct)}%)</span>
          </span>
          <div className="flex-1 border-t" style={{ borderColor: "var(--warn, #f39c12)" }} />
        </div>
      </td>
    </tr>
  );
  const body = [];
  data.rows.forEach((r, i) => {
    if (i === data.spot_index) body.push(marker);
    body.push(
      <tr key={r.strike} className="border-b border-grid/40">
        <Leg leg={r.ce} side="CE" maxOi={data.max_oi} onPick={onPick} selected={r.ce?.tradingsymbol === selectedTs} />
        <td className="px-3 py-1.5 text-center font-semibold"
            style={{ color: r.strike === data.atm_strike ? "var(--bull)" : undefined, background: "rgba(255,255,255,0.02)" }}>
          {fmt(r.strike, 2)}
        </td>
        <Leg leg={r.pe} side="PE" maxOi={data.max_oi} onPick={onPick} selected={r.pe?.tradingsymbol === selectedTs} />
      </tr>,
    );
  });
  if (data.spot_index === data.rows.length) body.push(marker);
  return (
    <div className="border border-grid overflow-auto max-h-[70vh]" data-testid="mo-chain">
      <table className="w-full text-[11px] font-mono-data">
        {/* opaque cells: rows scrolling under the sticky header must not show through */}
        <thead className="sticky top-0 z-10">
          <tr className="text-[9px] uppercase tracking-widest">
            <th colSpan={3} className="py-1.5" style={{ ...TH_BG, color: "var(--bull)" }}>Call</th>
            <th className="py-1.5 text-muted-3" style={TH_BG}>Strike</th>
            <th colSpan={3} className="py-1.5" style={{ ...TH_BG, color: "var(--bear)" }}>Put</th>
          </tr>
          <tr className="text-[9px] uppercase tracking-widest text-muted-3">
            <th className="px-2 py-1 text-right" style={TH_BG2}>OI {data.segment === "NFO" ? "(lots)" : ""}</th>
            <th className="px-2 py-1 text-right" style={TH_BG2}>LTP</th><th className="px-1 py-1" style={TH_BG2} />
            <th style={TH_BG2} />
            <th className="px-1 py-1" style={TH_BG2} /><th className="px-2 py-1 text-right" style={TH_BG2}>LTP</th>
            <th className="px-2 py-1 text-right" style={TH_BG2}>OI {data.segment === "NFO" ? "(lots)" : ""}</th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
};

// ───────────── working orders for the open contract ─────────────
const WorkingOrders = ({ orders, account, onDone }) => {
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e, "Failed")); } finally { onDone(); }
  };
  const cancel = (o) => {
    if (!window.confirm(`Cancel ${o.transaction_type} ${o.quantity} @ ₹${o.price} (order ${o.order_id})?`)) return;
    act(() => axios.post(`${API}/kite-console/${account.account_id}/orders/${o.order_id}/cancel`), `Cancelled ${o.order_id}`);
  };
  const modify = (o) => {
    const p = window.prompt(`New LIMIT price for ${o.tradingsymbol} (now ₹${o.price}):`, String(o.price || ""));
    if (!p) return;
    // 2026-11-02 — quantity can be changed too (e.g. grow a working SELL after adding lots)
    const q = window.prompt(`Quantity for ${o.transaction_type} ${o.tradingsymbol} (now ${o.quantity}, filled ${o.filled_quantity || 0}):`, String(o.quantity || ""));
    if (q === null) return;
    const body = { price: Number(p), order_type: "LIMIT", ...(q && Number(q) !== Number(o.quantity) ? { quantity: Number(q) } : {}) };
    act(() => axios.post(`${API}/kite-console/${account.account_id}/orders/${o.order_id}/modify`, body),
        `Modified ${o.order_id} → ₹${p}${body.quantity ? ` × ${body.quantity}` : ""}`);
  };
  return (
    <div className="border border-grid/60 p-2 space-y-1" data-testid="mo-working-orders">
      <div className="text-[9px] uppercase tracking-widest text-muted-3">Working orders · {account?.label}</div>
      {orders.map((o) => (
        <div key={o.order_id} className="flex items-center justify-between gap-2 text-[11px]">
          <span>
            <span style={{ color: o.transaction_type === "BUY" ? "var(--bull)" : "var(--bear)" }}>{o.transaction_type}</span>
            {" "}{o.filled_quantity || 0}/{o.quantity} @ ₹{fmt(o.price)} · <span className="text-muted-3">{o.status}</span>
          </span>
          <span className="space-x-1 whitespace-nowrap">
            {o.order_type === "LIMIT" && <button className="px-1.5 py-0.5 border border-grid text-[10px]" onClick={() => modify(o)}>Modify</button>}
            <button className="px-1.5 py-0.5 border text-[10px]" style={{ borderColor: "var(--bear)", color: "var(--bear)" }}
                    onClick={() => cancel(o)}>Cancel</button>
          </span>
        </div>
      ))}
    </div>
  );
};

// ───────────── contract panel ─────────────
const Depth = ({ q }) => {
  const rows = Array.from({ length: 5 }, (_, i) => [q.bids?.[i], q.asks?.[i]]);
  const unit = q.lot_size > 1 ? "lots" : "qty";
  return (
    <div className="font-mono-data text-[11px]" data-testid="mo-depth">
      <div className="grid grid-cols-4 text-[9px] uppercase tracking-widest text-muted-3 pb-1">
        <span>{unit}</span><span className="text-right">Bid</span><span className="text-right">Offer</span><span className="text-right">{unit}</span>
      </div>
      {rows.map(([b, a], i) => (
        <div key={i} className="grid grid-cols-4 py-0.5">
          <span style={{ color: "var(--bull)" }}>{b ? fmt(b.lots, 2) : "—"}</span>
          <span className="text-right" style={{ color: "var(--bull)" }}>{b ? fmt(b.price) : "—"}</span>
          <span className="text-right" style={{ color: "var(--bear)" }}>{a ? fmt(a.price) : "—"}</span>
          <span className="text-right" style={{ color: "var(--bear)" }}>{a ? fmt(a.lots, 2) : "—"}</span>
        </div>
      ))}
      {q.buy_pct != null && (
        <div className="mt-2">
          <div className="flex h-2 w-full overflow-hidden rounded-sm">
            <div style={{ width: `${q.buy_pct}%`, background: "var(--bull)" }} />
            <div style={{ width: `${100 - q.buy_pct}%`, background: "var(--bear)" }} />
          </div>
          <div className="flex justify-between text-[9px] text-muted-3 mt-1">
            <span>Bid {fmt(q.buy_pct)}% · TBQ {fmtQty(q.total_buy_qty)}</span>
            <span>TSQ {fmtQty(q.total_sell_qty)} · {fmt(100 - q.buy_pct)}% Offer</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ k, v, c }) => (
  <div>
    <div className="text-[9px] uppercase tracking-widest text-muted-3">{k}</div>
    <div className="mt-0.5" style={c ? { color: c } : undefined}>{v}</div>
  </div>
);

// 2026-10-31 — reason tag (stored on the order, P&L split by reason) + "also place in"
// other Kite accounts (same contract / lots / type / price, sent at the same moment).
const FALLBACK_REASONS = {
  entry: ["Breakout", "Reversal at support/resistance", "Trend continuation", "News / event",
          "Momentum / OI build-up", "Copying an autopilot signal", "Scalp", "Gut feel"],
  exit: ["Target hit", "Stop loss", "Trailing / locking profit", "Reversal signal", "Time / end of day",
         "Fear / uncertainty", "Booked early"],
};
let _reasonsCache = null;

const ContractPanel = ({ pick, account, accounts = [], segment, position, orders, onPlaced, onClose }) => {
  const [q, setQ] = useState(null);
  const [reasons, setReasons] = useState(_reasonsCache || FALLBACK_REASONS);
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [alsoIds, setAlsoIds] = useState([]);
  // 2026-10-31 — every other account is listed; one not logged in to Kite today is shown
  // disabled (it used to be hidden, so the option seemed to be missing)
  const otherAll = accounts.filter((a) => a.account_id !== account?.account_id);
  const others = otherAll.filter((a) => a.connected);
  useEffect(() => {
    if (_reasonsCache) return;
    axios.get(`${API}/kite-console/reasons`).then((r) => { _reasonsCache = r.data; setReasons(r.data); }).catch(() => {});
  }, []);
  useEffect(() => { setAlsoIds((ids) => ids.filter((id) => others.some((a) => a.account_id === id))); },
            [account?.account_id, others.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const reasonText = reason === "__other" ? otherReason.trim() : reason;
  const [lots, setLots] = useState(1);
  const [otype, setOtype] = useState("LIMIT");
  const [price, setPrice] = useState("");
  const [product, setProduct] = useState("NRML");
  const [busy, setBusy] = useState(false);
  const priceTouched = useRef(false);

  useEffect(() => { priceTouched.current = false; setPrice(""); setQ(null); }, [pick.tradingsymbol, pick.side]);
  useVisiblePoll(async () => {
    try {
      const { data } = await axios.get(`${API}/kite-console/quote`, { params: { exchange: segment, tradingsymbol: pick.tradingsymbol } });
      setQ(data);
    } catch { /* keep last */ }
  }, 2500, [pick.tradingsymbol, segment]);

  // Until the user types a price: BUY goes at the best offer, SELL at the best bid (else LTP).
  const autoPrice = (s) => (s === "BUY" ? q?.asks?.[0]?.price : q?.bids?.[0]?.price) || q?.ltp || null;
  useEffect(() => {
    if (!q || priceTouched.current) return;
    const p = autoPrice(pick.side);
    if (p) setPrice(String(p));
  }, [q, pick.side]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (side) => {
    if (!account?.connected) { toast.error(`${account?.label || "Account"} isn't logged in to Kite today`); return; }
    const px = priceTouched.current ? Number(price) : Number(autoPrice(side) || price);
    if (otype === "LIMIT" && !(px > 0)) { toast.error("Enter a limit price"); return; }
    const payload = { exchange: segment, tradingsymbol: pick.tradingsymbol, transaction_type: side, lots: Number(lots),
                      order_type: otype, price: otype === "LIMIT" ? px : 0, product, reason: reasonText || undefined };
    const targets = [account, ...others.filter((a) => alsoIds.includes(a.account_id))];
    setBusy(true);
    try {
      const marginLines = [];
      if (side === "BUY") {
        for (const a of targets) {
          try {
            const { data: m } = await axios.post(`${API}/kite-console/${a.account_id}/margin`, payload);
            marginLines.push(`${a.label}: margin ₹${fmt(m.total, 0)}`);
          } catch (e) { marginLines.push(`${a.label}: margin check failed (${errMsg(e, "unknown")})`); }
        }
      }
      const qty = Number(lots) * (q?.lot_size || 1);
      const who = targets.map((a) => a.label).join(" + ");
      if (!window.confirm(`REAL ${side} on ${who}\n\n${pick.tradingsymbol} (${segment})\n${lots} lot (${qty}) each · ${otype}` +
                          `${otype === "LIMIT" ? ` @ ₹${px}` : ""} · ${product}` +
                          `${reasonText ? `\nReason: ${reasonText}` : "\nReason: (none)"}` +
                          `${marginLines.length ? `\n${marginLines.join("\n")}` : ""}` +
                          `\n\nThis places a real order in ${targets.length > 1 ? "each of these Zerodha accounts" : "this Zerodha account"}.`)) return;
      // all accounts at the same moment; each one's result is reported on its own
      const results = await Promise.allSettled(targets.map((a) => axios.post(`${API}/kite-console/${a.account_id}/order`, payload)));
      results.forEach((r, i) => {
        const label = targets.length > 1 ? `${targets[i].label}: ` : "";
        if (r.status === "rejected") { toast.error(`${label}${errMsg(r.reason, "Order rejected")}`); return; }
        const data = r.value.data;
        const st = String(data.status || "open").toLowerCase();
        if (st === "complete") {
          toast.success(`${label}${side} filled · ${data.filled_qty ?? qty} @ ₹${fmt(data.avg_price)} · order ${data.order_id}`);
        } else if (st === "rejected" || st === "cancelled") {
          toast.error(`${label}${side} ${st.toUpperCase()} by Kite · ${data.status_message || ""} · order ${data.order_id}`);
        } else {
          toast.success(`${label}${side} placed · order ${data.order_id} · ${st.toUpperCase()} — working, not filled yet`,
                        { duration: 8000 });
        }
      });
      onPlaced();
      setTimeout(onPlaced, 3000);
    } catch (e) {
      toast.error(errMsg(e, "Order rejected"));
    } finally { setBusy(false); }
  };

  const title = q?.strike ? `${q.underlying} ${q.expiry} ${fmt(q.strike, 2)} ${q.option_type}` : pick.tradingsymbol;
  return (
    <div className="border border-grid flex flex-col" data-testid="mo-contract">
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-semibold font-mono-data">{title}</div>
            <div className="text-[10px] text-muted-3 font-mono-data">{segment} OPT · {pick.tradingsymbol} · lot {q?.lot_size ?? "—"}</div>
          </div>
          <button className="text-muted-3 text-xs" onClick={onClose}>✕</button>
        </div>
        <div className="flex items-baseline gap-3 font-mono-data">
          <span className="text-2xl">{fmt(q?.ltp)}</span>
          <span className="text-sm" style={{ color: chgColor(q?.change) }}>
            {q?.change != null ? `${q.change >= 0 ? "+" : ""}${fmt(q.change)} (${fmt(q.chg_pct)}%)` : ""}
          </span>
        </div>
        {q && !q.traded_today && (
          <div className="flex items-center gap-2 text-[11px] font-mono-data px-2 py-1 border" style={{ color: "var(--warn, #f39c12)", borderColor: "var(--warn, #f39c12)" }}>
            <AlertTriangle size={12} /> No trades in this contract today — LTP is stale; check the depth before ordering.
          </div>
        )}
        {q ? <Depth q={q} /> : <div className="text-xs text-muted-3 font-mono-data">Loading quote…</div>}
        {q && (
          <div className="grid grid-cols-3 gap-3 font-mono-data text-[11px] pt-2 border-t border-grid/60" data-testid="mo-details">
            <Stat k="Open" v={fmt(q.open)} /><Stat k="High" v={fmt(q.high)} c="var(--bull)" /><Stat k="Low" v={fmt(q.low)} c="var(--bear)" />
            <Stat k="Prev close" v={fmt(q.prev_close)} /><Stat k="Avg traded" v={fmt(q.avg_price)} /><Stat k="Volume" v={fmtQty(q.volume)} />
            <Stat k={`OI${q.lot_size > 1 ? " (lots)" : ""}`} v={fmtQty(q.oi_lots)} />
            <Stat k="Lower circuit" v={fmt(q.lower_circuit)} /><Stat k="Upper circuit" v={fmt(q.upper_circuit)} />
            <div className="col-span-3"><Stat k="Last traded" v={`${q.last_trade_time || "—"}${q.last_qty ? ` · qty ${q.last_qty}` : ""}`} /></div>
          </div>
        )}
        <div className="pt-2 border-t border-grid/60 space-y-2 font-mono-data text-xs">
          {position != null && position !== 0 && (
            <div className="text-[11px]">Open position in {account?.label}: <span style={{ color: chgColor(position) }}>{position}</span></div>
          )}
          {(orders || []).length > 0 && <WorkingOrders orders={orders} account={account} onDone={onPlaced} />}
          <label className="block" data-testid="mo-reason">
            <div className="text-[9px] uppercase text-muted-3">Reason (saved with the order · P&amp;L by reason)</div>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-transparent border border-grid px-2 py-1">
              <option value="">— pick a reason —</option>
              <optgroup label={position > 0 ? "Exit / reduce" : "Entry"}>
                {(position > 0 ? reasons.exit : reasons.entry).map((r) => <option key={r} value={r}>{r}</option>)}
              </optgroup>
              <optgroup label={position > 0 ? "Entry (adding)" : "Exit"}>
                {(position > 0 ? reasons.entry : reasons.exit).map((r) => <option key={`x-${r}`} value={r}>{r}</option>)}
              </optgroup>
              <option value="__other">Other…</option>
            </select>
            {reason === "__other" && (
              <input value={otherReason} maxLength={60} onChange={(e) => setOtherReason(e.target.value)} placeholder="Your reason (60 chars)"
                     className="w-full mt-1 bg-transparent border border-grid px-2 py-1" />
            )}
          </label>
          {otherAll.length > 0 && (
            <div className="space-y-0.5" data-testid="mo-also-accounts">
              {otherAll.map((a) => (
                <label key={a.account_id} className={`flex items-center gap-2 text-[11px] ${a.connected ? "cursor-pointer" : "opacity-50"}`}
                       title={a.connected ? "" : `${a.label} isn't logged in to Kite today — log in from Live Orders → Kite accounts`}>
                  <input type="checkbox" disabled={!a.connected} checked={a.connected && alsoIds.includes(a.account_id)}
                         onChange={(e) => setAlsoIds((ids) => (e.target.checked ? [...ids, a.account_id] : ids.filter((x) => x !== a.account_id)))} />
                  Also place in <span className="text-muted-1">{a.label}</span>
                  <span className="text-muted-3">{a.connected ? "(same lots, at the same moment)" : "— Kite login needed today"}</span>
                </label>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <label><div className="text-[9px] uppercase text-muted-3">Lots</div>
              <input type="number" min={1} max={20} value={lots} onChange={(e) => setLots(e.target.value)}
                     className="w-full bg-transparent border border-grid px-2 py-1" data-testid="mo-lots" /></label>
            <label><div className="text-[9px] uppercase text-muted-3">Type</div>
              <select value={otype} onChange={(e) => setOtype(e.target.value)} className="w-full bg-transparent border border-grid px-2 py-1">
                <option value="LIMIT">LIMIT</option><option value="MARKET">MARKET</option></select></label>
            <label><div className="text-[9px] uppercase text-muted-3">Product</div>
              <select value={product} onChange={(e) => setProduct(e.target.value)} className="w-full bg-transparent border border-grid px-2 py-1">
                <option value="NRML">NRML</option><option value="MIS">MIS</option></select></label>
          </div>
          {otype === "LIMIT" && (
            <label className="block"><div className="text-[9px] uppercase text-muted-3">Limit price ₹</div>
              <div className="flex gap-2">
                <input type="number" step="0.05" value={price} onChange={(e) => { priceTouched.current = true; setPrice(e.target.value); }}
                       className="flex-1 bg-transparent border border-grid px-2 py-1" data-testid="mo-price" />
                {priceTouched.current && (
                  <button className="px-2 border border-grid text-[10px]" onClick={() => { priceTouched.current = false; setPrice(String(autoPrice(pick.side) || "")); }}>auto</button>
                )}
              </div>
              <div className="text-[9px] text-muted-3 mt-0.5">
                {priceTouched.current ? "Your price is used for Buy and Sell"
                  : `Auto: Buy at best offer ₹${fmt(autoPrice("BUY"))} · Sell at best bid ₹${fmt(autoPrice("SELL"))}`}
              </div>
            </label>
          )}
          <div className="text-[10px] text-muted-3">
            {fmt(Number(lots) * (q?.lot_size || 1), 0)} qty · SELL only reduces a long you hold (no option writing) · BUY blocked during a hard-kill
            {account?.max_margin_rs ? ` · margin cap ₹${fmt(account.max_margin_rs, 0)}` : ""}
          </div>
        </div>
      </div>
      {/* sticky big Buy/Sell (mobile-friendly) */}
      <div className="sticky bottom-0 grid grid-cols-2 border-t border-grid" style={{ background: "var(--bg-primary, #0b0b0b)" }}>
        <button disabled={busy || !account?.connected} onClick={() => submit("BUY")}
                className="py-3 text-sm font-semibold uppercase disabled:opacity-40" data-testid="mo-buy"
                style={{ background: "var(--bull)", color: "#04120a" }}>
          {busy ? <RefreshCw size={14} className="inline animate-spin" /> : `Buy ${lots} lot${alsoIds.length ? ` × ${alsoIds.length + 1} accts` : ""}`}
        </button>
        <button disabled={busy || !account?.connected} onClick={() => submit("SELL")}
                className="py-3 text-sm font-semibold uppercase disabled:opacity-40" data-testid="mo-sell"
                style={{ background: "var(--bear)", color: "#160404" }}>
          {busy ? <RefreshCw size={14} className="inline animate-spin" /> : `Sell ${lots} lot${alsoIds.length ? ` × ${alsoIds.length + 1} accts` : ""}`}
        </button>
      </div>
      {!account?.connected && <div className="px-4 py-2 text-[10px] font-mono-data" style={{ color: "var(--bear)" }}>Log this account in to Kite (Live Orders → Kite accounts) to trade.</div>}
    </div>
  );
};

// ───────────── page ─────────────
export const TradeTerminal = () => {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [segment, setSegment] = useState("NFO");
  const [und, setUnd] = useState("NIFTY");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [recent, setRecent] = useState([]);
  const [expiry, setExpiry] = useState("");
  const [width, setWidth] = useState(10);
  const [chain, setChain] = useState(null);
  const [pick, setPick] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kite-accounts`).then(({ data }) => {
      const list = (data.accounts || []).filter((a) => a.enabled !== false);
      setAccounts(list);
      setAccId((cur) => cur || list[0]?.account_id || null);
    }).catch(() => setAccounts([]));
  }, []);
  const account = useMemo(() => accounts.find((a) => a.account_id === accId), [accounts, accId]);

  const loadSummary = useCallback(async () => {
    if (!accId) return;
    try { const { data } = await axios.get(`${API}/kite-console/${accId}/summary`); setSummary(data); } catch { setSummary(null); }
  }, [accId]);
  // faster while an order is working, so a fill / cancel shows up within seconds
  const hasOpenOrder = (summary?.orders || []).some((o) => o.is_open);
  useVisiblePoll(loadSummary, hasOpenOrder ? 5000 : 20000, [loadSummary, hasOpenOrder]);

  // underlying search (NFO)
  useEffect(() => {
    if (segment !== "NFO" || !query) { setHits([]); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/kite-console/underlyings`, { params: { segment, q: query } });
        setHits(data.underlyings || []);
      } catch { setHits([]); }
    }, 200);
    return () => clearTimeout(id);
  }, [query, segment]);

  const chooseUnd = (u) => {
    setUnd(u); setExpiry(""); setChain(null); setPick(null); setQuery(""); setHits([]);
    if (segment === "NFO") setRecent((r) => [u, ...r.filter((x) => x !== u)].slice(0, 6));
  };
  const switchSegment = (s) => {
    setSegment(s); setExpiry(""); setChain(null); setPick(null);
    setUnd(s === "MCX" ? "CRUDEOIL" : (recent[0] || "NIFTY"));
  };

  useVisiblePoll(async () => {
    if (!und) return;
    try {
      const { data } = await axios.get(`${API}/kite-console/chain`, { params: { segment, underlying: und, expiry: expiry || undefined, width } });
      setChain(data);
      if (!expiry && data.expiry) setExpiry(data.expiry);
    } catch (e) { setChain({ rows: [], error: errMsg(e, "Chain unavailable") }); }
  }, 4000, [segment, und, expiry, width]);

  const positionFor = (ts) => {
    const p = (summary?.open_positions || []).filter((x) => x.tradingsymbol === ts);
    return p.length ? p.reduce((s, x) => s + Number(x.quantity || 0), 0) : 0;
  };
  // 2026-10-30 — chart lines: average price of the open position + working order prices
  const avgPriceFor = (ts) => {
    const p = (summary?.open_positions || []).find((x) => x.tradingsymbol === ts && Number(x.quantity || 0) !== 0);
    return p ? Number(p.average_price || 0) : null;
  };
  const funds = summary?.funds;
  const avail = segment === "MCX" ? funds?.commodity_available : funds?.equity_available;
  const onPick = (leg, side) => setPick({ tradingsymbol: leg.tradingsymbol, side });
  const chips = segment === "MCX" ? MCX : Array.from(new Set([...NFO_QUICK, ...recent]));

  return (
    <section className="space-y-4" data-testid="manual-order-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">Trading terminal · Kite</div>
          <h2 className="font-heading text-2xl flex items-center gap-2"><ShoppingCart size={20} /> Manual Order</h2>
        </div>
        {account && (
          <div className="text-[11px] font-mono-data text-muted-2 flex items-center gap-3">
            <span className="flex items-center gap-1"><Wallet size={11} /> Available ₹{fmt(avail, 0)}</span>
            <span style={{ color: account.connected ? "var(--bull)" : "var(--bear)" }}>{account.connected ? "Kite connected" : "Kite login needed"}</span>
          </div>
        )}
      </div>

      {!accounts.length && (
        <div className="border border-grid p-6 text-xs text-muted-3 font-mono-data">
          No Kite account available. Add your Zerodha API credentials in Settings and ask the admin to enable it.
        </div>
      )}

      {accounts.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data mr-1">Account</span>
            {accounts.map((a) => (
              <button key={a.account_id} onClick={() => setAccId(a.account_id)} className={pill} data-testid={`mo-account-${a.account_id}`}
                      style={{ borderColor: a.account_id === accId ? "var(--bull)" : "var(--grid)", color: a.account_id === accId ? "var(--bull)" : undefined }}>
                {a.label}
                <span className="inline-block w-1.5 h-1.5 rounded-full ml-2 align-middle" style={{ background: a.connected ? "var(--bull)" : "var(--bear)" }} />
              </button>
            ))}
            <span className="mx-2 h-5 border-l border-grid" />
            {[["NFO", "Stocks"], ["MCX", "Commodities"]].map(([s, l]) => (
              <button key={s} onClick={() => switchSegment(s)} className={pill} data-testid={`mo-seg-${s}`}
                      style={{ borderColor: segment === s ? "var(--bull)" : "var(--grid)", color: segment === s ? "var(--bull)" : undefined }}>{l}</button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {segment === "NFO" && (
              <div className="relative">
                <div className="flex items-center gap-2 border border-grid px-2 py-1.5 font-mono-data">
                  <Search size={13} className="text-muted-3" />
                  <input value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())} placeholder="Search stock / index…"
                         className="bg-transparent outline-none text-xs w-44" data-testid="mo-search-input" />
                </div>
                {hits.length > 0 && (
                  <div className="absolute z-20 mt-1 w-56 border border-grid max-h-72 overflow-y-auto" style={{ background: "var(--bg-primary, #0b0b0b)" }}>
                    {hits.map((h) => (
                      <button key={h} onClick={() => chooseUnd(h)} className="block w-full text-left px-3 py-1.5 text-xs font-mono-data hover:bg-white/5">{h}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {chips.map((u) => (
              <button key={u} onClick={() => chooseUnd(u)} className={pill}
                      style={{ borderColor: und === u ? "var(--bull)" : "var(--grid)", color: und === u ? "var(--bull)" : undefined }}>{u}</button>
            ))}
            <span className="mx-1 h-5 border-l border-grid" />
            <span className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data">Expiry</span>
            <select value={expiry} onChange={(e) => { setExpiry(e.target.value); setPick(null); }}
                    className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data" data-testid="mo-expiry-select">
              {(chain?.expiries || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select value={width} onChange={(e) => setWidth(Number(e.target.value))} className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data">
              {[5, 10, 15, 20].map((w) => <option key={w} value={w}>±{w} strikes</option>)}
            </select>
          </div>

          {chain?.spot?.ltp != null && (
            <div className="flex flex-wrap items-baseline gap-3 font-mono-data">
              <span className="text-lg font-semibold">{und}</span>
              <span className="text-lg">{fmt(chain.spot.ltp)}</span>
              <span style={{ color: chgColor(chain.spot.change) }}>
                {chain.spot.change != null ? `${chain.spot.change >= 0 ? "+" : ""}${fmt(chain.spot.change)} (${fmt(chain.spot.chg_pct)}%)` : ""}
              </span>
              <span className="text-[10px] text-muted-3">{segment === "MCX" ? `vs ${String(chain.spot.symbol || "").replace("MCX:", "")}` : ""} · shaded = in the money · faded = no trades today / empty book</span>
            </div>
          )}

          <div className={`grid gap-4 ${pick ? "lg:grid-cols-[1fr_400px]" : ""}`}>
            <div className="space-y-4 min-w-0">
              {pick && (
                <PriceChart exchange={segment} tradingsymbol={pick.tradingsymbol}
                            avgPrice={avgPriceFor(pick.tradingsymbol)}
                            orderPrices={(summary?.orders || []).filter((o) => o.is_open && o.tradingsymbol === pick.tradingsymbol)
                              .map((o) => ({ price: Number(o.price || 0), side: String(o.transaction_type || "").toUpperCase() }))} />
              )}
              <Chain data={chain} onPick={onPick} selectedTs={pick?.tradingsymbol} />
            </div>
            {pick && (
              <div className="lg:sticky lg:top-4 self-start">
                <ContractPanel pick={pick} account={account} accounts={accounts} segment={segment} position={positionFor(pick.tradingsymbol)}
                               orders={(summary?.orders || []).filter((o) => o.is_open && o.tradingsymbol === pick.tradingsymbol)}
                               onPlaced={loadSummary} onClose={() => setPick(null)} />
              </div>
            )}
          </div>
          <div className="text-[10px] text-muted-3 font-mono-data">
            Open positions, working orders (modify / cancel) and exits for each account: Live Orders → Kite accounts console.
          </div>
        </>
      )}
    </section>
  );
};

export default TradeTerminal;
